import { createStore } from '@xstate/store'
import { User } from '@workspace/lib/api'
import { api } from '@/shared/lib/api'
import { useQuery } from '@tanstack/react-query'
import { isAuthenticated } from '@/shared/lib/auth'
import { useEffect, useSyncExternalStore } from 'react'
import { useSelector } from '@xstate/store/react'
import { queryClient } from '@/queryClient'

// Note: We import clearProactiveRefresh dynamically to avoid circular dependencies
let clearProactiveRefresh: (() => void) | null = null

// Set the function after tokenService is initialized
export const setTokenServiceClearFunction = (fn: () => void) => {
    clearProactiveRefresh = fn
}

type AuthState = {
    user: User | null
    token: string | null
    refreshToken: string | null
    expiresIn: number | null
    isAuthenticated: boolean
}

const STORAGE_KEY = 'pmsvb_auth'

// Load initial state from localStorage
const loadInitialState = (): AuthState => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
            const data = JSON.parse(stored)
            return {
                user: data.user || null,
                token: data.token || null,
                refreshToken: data.refreshToken || null,
                expiresIn: data.expiresIn || null,
                isAuthenticated: !!data.token,
            }
        }
    } catch (error) {
        console.error('Failed to load auth state from localStorage:', error)
    }
    return {
        user: null,
        token: null,
        refreshToken: null,
        expiresIn: null,
        isAuthenticated: false,
    }
}

// Save state to localStorage
const saveToLocalStorage = (state: AuthState) => {
    try {
        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
                user: state.user,
                token: state.token,
                refreshToken: state.refreshToken,
                expiresIn: state.expiresIn,
            }),
        )
    } catch (error) {
        console.error('Failed to save auth state to localStorage:', error)
    }
}

type AuthEvent =
    | { type: 'login'; user: User; token: string; refreshToken: string; expiresIn: number }
    | { type: 'logout' }
    | { type: 'updateUser'; user: User }
    | { type: 'updateTokens'; token: string; expiresIn: number; refreshToken: string }

export const authStore = createStore<AuthState, AuthEvent, any>({
    context: loadInitialState(),
    on: {
        login: (context, event: { user: User; token: string; refreshToken: string; expiresIn: number }) => {
            const newState = {
                user: event.user,
                token: event.token,
                refreshToken: event.refreshToken,
                expiresIn: event.expiresIn,
                isAuthenticated: true,
            }
            saveToLocalStorage(newState)
            return newState
        },
        logout: () => {
            const newState = {
                user: null,
                token: null,
                refreshToken: null,
                expiresIn: null,
                isAuthenticated: false,
            }
            saveToLocalStorage(newState)
            return newState
        },
        updateUser: (context, event: { user: User }) => {
            const newState = {
                ...context,
                user: event.user,
            }
            saveToLocalStorage(newState)
            return newState
        },
        updateTokens: (context, event: { token: string; expiresIn: number; refreshToken: string }) => {
            const newState = {
                ...context,
                token: event.token,
                expiresIn: event.expiresIn,
                refreshToken: event.refreshToken,
            }
            saveToLocalStorage(newState)
            return newState
        },
    },
})

// Convenience functions
export const login = (user: User, token: string, refreshToken: string, expiresIn: number) => {
    authStore.send({ type: 'login', user, token, refreshToken, expiresIn })
}

export const logout = async (options?: { skipApi?: boolean; skipRedirect?: boolean }) => {
    try {
        // Get refresh token before clearing state
        const refreshToken = selectRefreshToken()

        // Call logout API if refresh token exists AND we are not skipping API call
        if (refreshToken && !options?.skipApi) {
            await api.auth.logout({ refreshToken })
        }
    } catch (error) {
        console.error('Failed to call logout API:', error)
        // Continue with logout even if API call fails
    } finally {
        // Clear proactive refresh timer if available
        if (clearProactiveRefresh) {
            clearProactiveRefresh()
        }

        // Clear all React Query cache to prevent data leakage between users
        queryClient.clear()

        authStore.send({ type: 'logout' })

        // Only redirect if not explicitly skipping
        if (!options?.skipRedirect) {
            window.location.href = '/login'
        }
    }
}

export const updateUser = (user: User) => {
    authStore.send({ type: 'updateUser', user })
}

export const updateTokens = (token: string, expiresIn: number, refreshToken: string) => {
    authStore.send({ type: 'updateTokens', token, expiresIn, refreshToken })
}

export const refreshUser = () => {
    // Get current user id from store to use in query key
    const currentUser = useSelector(authStore, state => state.context.user)
    const userId = currentUser?.id || 'anonymous'

    try {
        const { data: user } = useQuery({
            queryKey: ['user', 'refresh', userId],
            queryFn: () => api.auth.getMe(),
            enabled: isAuthenticated(),
            staleTime: 1000 * 60 * 5,
            gcTime: 1000 * 60 * 30,
            retry: 2,
        })

        useEffect(() => {
            if (user) {
                updateUser(user)
            }
        }, [user])
    } catch (error) {
        console.error('Failed to refresh user:', error)
        logout()
    }

    return currentUser
}

// Selectors
export const selectAuthState = () => authStore.getSnapshot().context
export const selectUser = () => authStore.select(state => state.user).get()
export const selectToken = () => authStore.select(state => state.token).get()
export const selectRefreshToken = () => authStore.select(state => state.refreshToken).get()
export const selectExpiresIn = () => authStore.select(state => state.expiresIn).get()
export const selectIsAuthenticated = () => authStore.select(state => state.isAuthenticated).get()

// React Hooks - Subscribe to store changes
export const useAuthState = () => {
    return useSyncExternalStore(
        callback => {
            const subscription = authStore.subscribe(() => {
                callback()
            })
            return () => subscription.unsubscribe()
        },
        () => authStore.getSnapshot().context,
        () => authStore.getSnapshot().context,
    )
}

export const useUser = () => {
    return useSyncExternalStore(
        callback => {
            const subscription = authStore.subscribe(() => {
                callback()
            })
            return () => subscription.unsubscribe()
        },
        () => authStore.getSnapshot().context.user,
        () => authStore.getSnapshot().context.user,
    )
}

export const useToken = () => {
    return useSyncExternalStore(
        callback => {
            const subscription = authStore.subscribe(() => {
                callback()
            })
            return () => subscription.unsubscribe()
        },
        () => authStore.getSnapshot().context.token,
        () => authStore.getSnapshot().context.token,
    )
}

export const useIsAuthenticated = () => {
    return useSyncExternalStore(
        callback => {
            const subscription = authStore.subscribe(() => {
                callback()
            })
            return () => subscription.unsubscribe()
        },
        () => authStore.getSnapshot().context.isAuthenticated,
        () => authStore.getSnapshot().context.isAuthenticated,
    )
}
